import { Box, BoxProps, Flex, Grid, Text } from '@chakra-ui/react';
import { useMemo } from 'react';
import authApi from '~/authApi';
import Button from '~/components/Button';
import IconButton from '~/components/IconButton';
import { AddIcon, EditIcon } from '~/components/icons';
import { FEATURE_FLAGS, USER_ROLES } from '~/constants';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';

import {
  HighlightedContent,
  HighlightedContentProps,
} from './HighlightedContent';

interface Props extends BoxProps {
  sectionTitle: string;
  contents: HighlightedContentProps[];
  onEdit: () => void;
  isSharedView?: boolean;
}

export const HighlightedContentSection: React.FC<Props> = ({
  sectionTitle,
  contents,
  onEdit,
  isSharedView,
  ...props
}) => {
  const hasContents = contents.length > 0;

  const { isEnabled: isMediaPacksDeprecated } = useFeature(
    FEATURE_FLAGS.foamDeprecateMediaPacks,
  );
  const { isEnabled: isMediaKitsForCreatorsEnabled } = useFeature(
    FEATURE_FLAGS.foamCreatorMediaKits,
  );

  const canEditMediaPack = useMemo(() => {
    const userIsManager = authApi.user.roles.includes(USER_ROLES.talentManager);
    if (isSharedView) return false;

    if (userIsManager) {
      return !isMediaPacksDeprecated;
    }

    return !isMediaKitsForCreatorsEnabled;
  }, [isMediaPacksDeprecated, isMediaKitsForCreatorsEnabled, isSharedView]);

  if (!hasContents && isSharedView) {
    return null;
  }

  return (
    <Box {...props}>
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontWeight="medium" letterSpacing="wide" fontSize="3xl">
          {sectionTitle}
        </Text>
        {canEditMediaPack && (
          <IconButton
            borderRadius="full"
            boxSize={10}
            _hover={{
              backgroundColor: 'primary.50',
            }}
            variant="ghost"
            aria-label="Edit content"
            icon={<EditIcon onClick={onEdit} />}
          />
        )}
      </Flex>
      {hasContents ? (
        <Grid
          templateColumns={{
            base: 'repeat(1, 1fr)',
            md: 'repeat(2, 1fr)',
            xl: 'repeat(3, 1fr)',
          }}
          gridGap={{ base: 9, md: 4, lg: 6 }}
          width="100%"
          marginTop={10}
        >
          {contents.map((content) => (
            <HighlightedContent
              key={`highlighted-content-${content.contentId}`}
              {...content}
            />
          ))}
        </Grid>
      ) : (
        <Button
          marginTop={10}
          width="100%"
          cursor="pointer"
          justifyContent="center"
          alignItems="center"
          padding="76px"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="primary.500"
          rounded="xl"
          variant="ghost"
          onClick={onEdit}
          isDisabled={!canEditMediaPack}
        >
          <AddIcon boxSize={3} viewBox="0 0 12 12" marginRight={3} />
          <Text as="span" fontWeight="medium" fontSize="xl">
            Add content
          </Text>
        </Button>
      )}
    </Box>
  );
};
